/**
 * Iris on the practice tracks and the power course: every step and level
 * can be done by hand, by briefing Iris, or both (docs/foundations.md §9,
 * owner 2026-10-16, the same rule as the software course: docs/agent-first-plan.md).
 *
 * Iris here is a designed agent (docs/designed-agents.md): what she says and
 * proposes for each brief is written in advance, so the lesson is controlled
 * and the same for everyone. The copy never labels her either way. The
 * pattern is the software course's level 9: a vague brief gets a plausible
 * change that fails the test; a brief that states the constraints gets a
 * change that passes. Her change lands on the same controls the learner
 * uses and is checked exactly like theirs, so approving blindly is graded,
 * not trusted.
 *
 * English only for now, on the owner's instruction (2026-10-16): the briefs
 * are data here, not in the typed string catalogue, and read in English in
 * every language.
 */
import type { GridSettings } from './grid/model';
import { CITIES, type CityId, type Facing } from './power';
import type { Slot } from './civil';
import type { Upgrades } from './foundations';

/** What a step's controls can be set to. Each step reads only its own keys. */
export interface Patch {
  watts?: number;
  thickness?: number;
  brackets?: 2 | 3;
  volts?: number;
  cables?: number;
  d?: number;
  up?: Upgrades;
  speed?: number;
  kit?: boolean;
  spacing?: number;
  depth?: number;
  beam?: string;
  front?: number;
  back?: number;
  slots?: Slot[];
  facing?: Facing;
  tilt?: number;
  panels?: number;
  cap?: number;
  size?: number;
}

/** Facts a reply may depend on, from the step's current state. */
export interface AgentContext {
  city?: CityId;
}

export interface Brief<P = Patch> {
  id: string;
  /** What the learner sends. */
  ask: string;
  /** What Iris answers. */
  reply: string | ((ctx: AgentContext) => string);
  /** The change she proposes, if any. */
  apply?: P | ((ctx: AgentContext) => P);
  /** A one-line summary of the change, shown on the Apply button's line. */
  change?: string | ((ctx: AgentContext) => string);
}

const FACE: Record<CityId, Facing> = { cairo: 'S', berlin: 'S', tokyo: 'S', nairobi: 'N', sydney: 'N' };
const goodTilt = (city: CityId) => Math.round(Math.abs(CITIES[city].lat) / 10) * 10;
const CITY_NAME: Record<CityId, string> = { cairo: 'Cairo', berlin: 'Berlin', tokyo: 'Tokyo', nairobi: 'Nairobi', sydney: 'Sydney' };

export const BRIEFS: Record<string, Brief[]> = {
  /* ── Foundations ─────────────────────────────────────────────────────── */
  'foundations:estimate': [
    {
      id: 'vague',
      ask: 'Guess how much power a person gives off.',
      reply: 'A person is a big warm machine, like a small heater. I would say about 1,000 W.',
      apply: { watts: 1000 },
      change: 'Set the guess to 1,000 W',
    },
    {
      id: 'sum',
      ask: 'Work it out from food: 2,000 kcal a day, 4,200 J per kcal, 86,400 seconds in a day. Show the sum.',
      reply: '2,000 × 4,200 is 8.4 million joules a day. Spread over 86,400 seconds that is about 97 W. Nearest stop: 100 W.',
      apply: { watts: 100 },
      change: 'Set the guess to 100 W',
    },
  ],
  'foundations:loads': [
    {
      id: 'vague',
      ask: 'Pick a plank for this shelf.',
      reply: 'Books are not that heavy, and thinner is cheaper. 12 mm will do.',
      apply: { thickness: 12 },
      change: 'Set the plank to 12 mm',
    },
    {
      id: 'checked',
      ask: 'Find the thinnest plank whose sag stays under 5 mm with 25 kg of books over 1 m. Check each size.',
      reply: '12 mm sags 9.9 mm. 15 mm sags 5.0 mm, just over the limit. 18 mm sags 2.9 mm. 18 mm is the thinnest that holds.',
      apply: { thickness: 18 },
      change: 'Set the plank to 18 mm',
    },
  ],
  'foundations:power': [
    {
      id: 'vague',
      ask: 'Make this power line work.',
      reply: 'More cables carry more current. I will use all four, at a safe 230 V.',
      apply: { volts: 230, cables: 4 },
      change: '230 V on 4 cables',
    },
    {
      id: 'limits',
      ask: 'Keep every cable under 3,100 A and the voltage at or under 1,000,000 V, with as few cables as possible.',
      reply: 'Current is power over voltage. At 1,000,000 V, 3 GW is 3,000 A, under one cable’s 3,100 A. One cable.',
      apply: { volts: 1_000_000, cables: 1 },
      change: '1,000,000 V on 1 cable',
    },
  ],
  'foundations:margin': [
    {
      id: 'vague',
      ask: 'Choose a rope that holds 500 kg.',
      reply: '500 kg pulls with about 4.9 kN. A 4 mm rope breaks at 9.6 kN, about twice that. 4 mm holds it.',
      apply: { d: 4 },
      change: 'Set the rope to 4 mm',
    },
    {
      id: 'factor',
      ask: 'Choose the thinnest rope that breaks at no less than five times the load.',
      reply: 'Five times 4.9 kN is 24.5 kN. 6 mm breaks at 21.6 kN, short. 7 mm breaks at 29.4 kN. 7 mm.',
      apply: { d: 7 },
      change: 'Set the rope to 7 mm',
    },
  ],
  'foundations:weakest': [
    {
      id: 'vague',
      ask: 'Make the chain stronger.',
      reply: 'The rope and the hook carry the load, so I will put the upgrades there: rope twice, hook once.',
      apply: { up: { anchor: 0, rope: 2, shackle: 0, hook: 1 } },
      change: 'Rope ×2, hook ×1',
    },
    {
      id: 'weakest',
      ask: 'Find the weakest part first, and spend each upgrade where the chain would break next.',
      reply: 'The anchor holds 18 kN, the weakest: two upgrades take it to 34. Then the shackle, at 25, is weakest: one upgrade makes 33. Nothing is under 30 now.',
      apply: { up: { anchor: 2, rope: 0, shackle: 1, hook: 0 } },
      change: 'Anchor ×2, shackle ×1',
    },
  ],
  'foundations:tradeoff': [
    {
      id: 'vague',
      ask: 'Get to the airlock as fast as you can.',
      reply: 'Full speed, then. 11 m/s covers 120 m in about 11 seconds.',
      apply: { speed: 11, kit: false },
      change: '11 m/s, no kit',
    },
    {
      id: 'constraints',
      ask: 'Carry the kit, arrive within 20 s, and keep 4 m/s of fuel in reserve.',
      reply: 'With the kit the pack has 20 m/s in all. Keep 4 back and 16 is left for going and stopping, so at most 8 m/s. To cover 120 m in 20 s I need at least 6. I will fly at 7 m/s.',
      apply: { speed: 7, kit: true },
      change: '7 m/s, with the kit',
    },
  ],
  'foundations:check-ai': [
    {
      id: 'sure',
      ask: 'Check the sag over 1.5 m before we build it.',
      reply: 'Good call. Sag grows with the fourth power of the span, so 1.5 m sags about five times as much as 1 m: 14.8 mm against a 7.5 mm limit. A bracket in the middle halves the span. With three brackets the 18 mm plank sags 0.4 mm.',
      apply: { thickness: 18, brackets: 3 },
      change: '18 mm on 3 brackets',
    },
    {
      id: 'vague',
      ask: 'Just make it hold.',
      reply: 'Thicker always helps: 22 mm instead of 18, on the same two brackets.',
      apply: { thickness: 22, brackets: 2 },
      change: '22 mm on 2 brackets',
    },
  ],

  /* ── Civil ───────────────────────────────────────────────────────────── */
  'civil:rafters': [
    {
      id: 'vague',
      ask: 'Size the rafters so the roof is safe.',
      reply: 'Deep and close together is the safest roof: 250 mm deep, 300 mm apart.',
      apply: { spacing: 300, depth: 250 },
      change: '250 mm deep at 300 mm',
    },
    {
      id: 'constraints',
      ask: 'Stay under 7.5 MPa and 20 litres of timber per metre of roof. Find the cheapest that works.',
      reply: '200 mm at 600 mm: 5.4 MPa and 16.7 L/m. 150 mm at 400 mm also works, at 18.8 L/m. The first uses less timber.',
      apply: { spacing: 600, depth: 200 },
      change: '200 mm deep at 600 mm',
    },
  ],
  'civil:lintel': [
    {
      id: 'vague',
      ask: 'Pick a beam for the garage door.',
      reply: 'Timber matches the rest of the house. Two 50×200 timbers side by side.',
      apply: { beam: 'timber-2x200' },
      change: 'Timber 2 × 50×200',
    },
    {
      id: 'constraints',
      ask: 'Pick the lightest steel beam that stays under its stress limit and sags less than the span over 360.',
      reply: 'IPE 140 is overstressed over 3 m. IPE 160 reaches 124 of its 165 MPa and sags 6.9 mm against 8.3. IPE 160.',
      apply: { beam: 'ipe-160' },
      change: 'Steel IPE 160',
    },
  ],
  'civil:footings': [
    {
      id: 'vague',
      ask: 'Make both footings 500 mm, like the standard drawing.',
      reply: 'Done: 500 mm at the front and at the back.',
      apply: { front: 0.5, back: 0.5 },
      change: '500 mm front and back',
    },
    {
      id: 'soil',
      ask: 'Size each footing for its own soil: 150 kPa at the front, 75 kPa at the back.',
      reply: 'Front: 450 mm puts 145 kPa on the sand. Back: 1.0 m puts 72 kPa on the clay. The soft end needs more than twice the width.',
      apply: { front: 0.45, back: 1.0 },
      change: '450 mm front, 1.0 m back',
    },
  ],
  'civil:bracing': [
    {
      id: 'vague',
      ask: 'Brace this wall against the wind.',
      reply: 'Brace every panel. Eight braced panels resist 40 kN, twice the wind.',
      apply: { slots: ['brace', 'brace', 'brace', 'brace', 'brace', 'brace', 'brace', 'brace'] },
      change: 'Eight braced panels',
    },
    {
      id: 'constraints',
      ask: 'Resist the 19.2 kN, brace both corners, and keep three windows.',
      reply: 'Four braced panels give 20 kN: both corners, plus panels 3 and 7. Windows in 2, 4 and 6; panel 5 stays plain.',
      apply: { slots: ['brace', 'window', 'brace', 'window', 'plain', 'window', 'brace', 'brace'] },
      change: 'Braced 1, 3, 7, 8; windows 2, 4, 6',
    },
  ],
  'civil:civil-ai': [
    {
      id: 'check',
      ask: 'Check the stress at 3.6 m, not just the material.',
      reply: 'You are right. Bending grows with the square of the span: 20% wider is 44% more. IPE 140 reaches 251 MPa, over its 165. IPE 200 reaches 100 MPa and sags 6.4 mm of 10. I will change it.',
      apply: { beam: 'ipe-200' },
      change: 'Steel IPE 200',
    },
    {
      id: 'vague',
      ask: 'Go with timber instead, it is easier.',
      reply: 'Two 50×200 timbers, then.',
      apply: { beam: 'timber-2x200' },
      change: 'Timber 2 × 50×200',
    },
  ],

  /* ── Power and Energy ────────────────────────────────────────────────── */
  'power:tilt': [
    {
      id: 'vague',
      ask: 'Point the panel at the sun.',
      reply: 'The sun rises in the east, so I will face it east and tilt it 30°.',
      apply: { facing: 'E', tilt: 30 },
      change: 'East, 30°',
    },
    {
      id: 'rule',
      ask: 'Face the equator and tilt by about this city’s latitude.',
      reply: (ctx) => {
        const city = ctx.city ?? 'cairo';
        const lat = CITIES[city].lat;
        const t = goodTilt(city);
        return `${CITY_NAME[city]} is at ${Math.abs(lat).toFixed(1)}° ${lat >= 0 ? 'north' : 'south'}, so the equator is to the ${FACE[city] === 'S' ? 'south' : 'north'}. ${t === 0 ? 'So close to it, the panel lies flat.' : `Tilt ${t}°.`}`;
      },
      apply: (ctx) => ({ facing: FACE[ctx.city ?? 'cairo'], tilt: goodTilt(ctx.city ?? 'cairo') }),
      change: (ctx) => `${FACE[ctx.city ?? 'cairo'] === 'S' ? 'South' : 'North'}, ${goodTilt(ctx.city ?? 'cairo')}°`,
    },
  ],
  'power:array': [
    {
      id: 'vague',
      ask: 'Put as many panels up as we can.',
      reply: 'The roof takes 14. All 14, then.',
      apply: { panels: 14 },
      change: '14 panels',
    },
    {
      id: 'target',
      ask: 'Cover between 80% and 120% of the year’s 6,050 kWh.',
      reply: 'Each panel makes about 700 kWh a year here. 80% is 4,840 kWh, so at least 7 panels; 120% is 7,260, so at most 10. I will put up 8: about 5,600 kWh, 93%.',
      apply: { panels: 8 },
      change: '8 panels',
    },
  ],
  'power:battery': [
    {
      id: 'vague',
      ask: 'Make sure the house never goes dark.',
      reply: 'The biggest battery, then: 12.5 kWh.',
      apply: { cap: 12.5 },
      change: '12.5 kWh',
    },
    {
      id: 'constraints',
      ask: 'Cover the 6 kWh night using only 90% of the battery, within $4,000.',
      reply: '6 kWh at 90% usable needs 6.7 kWh of battery. 7.5 kWh is the smallest size that covers it, at $3,375.',
      apply: { cap: 7.5 },
      change: '7.5 kWh',
    },
  ],
  'power:cable': [
    {
      id: 'vague',
      ask: 'Use a normal house cable.',
      reply: 'Houses are wired with 2.5 mm². That one.',
      apply: { size: 2.5 },
      change: '2.5 mm²',
    },
    {
      id: 'constraints',
      ask: 'Carry 25 A safely and lose no more than 3% over the 25 m.',
      reply: '2.5 mm² is rated 24 A, just under 25. 4 mm² is rated 32 A and drops about 5.4 V, 2.3%. 4 mm².',
      apply: { size: 4 },
      change: '4 mm²',
    },
  ],
  'power:power-ai': [
    {
      id: 'check',
      ask: 'Check Berlin’s own numbers before reusing the plan.',
      reply: 'Berlin makes about 950 kWh per kW of panels a year, not Cairo’s 1,750, and its use peaks in winter. 8 panels cover only 75%. 10 panels at 40° cover about 95%.',
      apply: { panels: 10, tilt: 40 },
      change: '10 panels at 40°',
    },
    {
      id: 'vague',
      ask: 'Keep the Cairo plan, it worked.',
      reply: 'Keeping 8 panels at 30°.',
      apply: { panels: 8, tilt: 30 },
      change: '8 panels at 30°',
    },
  ],
};

/** Iris's briefs on the power course, which change the grid's settings directly. */
/** A brief on the power course: her change edits the grid's settings directly. */
export interface GridBrief extends Brief<true> {
  edit(s: GridSettings): void;
}

export const GRID_BRIEFS: Record<string, GridBrief[]> = {
  'dim-lights': [
    {
      id: 'vague',
      ask: 'Raise the voltage for Elm Street.',
      reply: 'Easiest at the substation: two steps up on the tap changer lifts the whole town by 2.5%.',
      apply: true,
      edit: (s) => {
        s.oltc = 5;
      },
      change: 'Substation tap +5',
    },
    {
      id: 'max',
      ask: 'Give Elm Street as much voltage as its transformer can.',
      reply: 'Elm Street’s transformer goes up to +5%. Setting it there.',
      apply: true,
      edit: (s) => {
        s.taps.elm = 4;
      },
      change: 'Elm Street tap +5%',
    },
    {
      id: 'constraints',
      ask: 'Fix Elm Street’s evening dip without pushing any street over 1.05 at night.',
      reply: 'Elm dips to 0.939 at 19:00 and rises to 1.014 at night. Its own transformer at +2.5% lifts it to about 0.962 at 19:00 and 1.039 at night, and leaves every other street as it is.',
      apply: true,
      edit: (s) => {
        s.taps.elm = 3;
      },
      change: 'Elm Street tap +2.5%',
    },
  ],
  'ev-hub': [
    {
      id: 'vague',
      ask: 'Connect the hub at the 3 MW they asked for.',
      reply: 'Feeder B is short, so it should take it. Leaving it at 3 MW.',
      apply: true,
      edit: (s) => {
        s.hubMw = 3;
      },
      change: 'Hub at 3 MW',
    },
    {
      id: 'move',
      ask: 'Move the hub onto Feeder A so Feeder B is not overloaded.',
      reply: 'Opening the switch between B1 and B2 and closing the tie puts Station Square and the hub on the end of Feeder A.',
      apply: true,
      edit: (s) => {
        s.closed['b1-b2'] = false;
        s.closed.tie = true;
      },
      change: 'Open B1–B2, close the tie',
    },
    {
      id: 'constraints',
      ask: 'Connect the hub without overloading anything, within $150,000. Find the cheapest way.',
      reply: 'At 3 MW, Feeder B section 1 reaches 106% at 19:00. A heavier line there costs $120,000. Or the hub charges at 2 MW: the same 12 MWh in 6 hours, done by 23:00, and the section peaks well under its rating. That costs nothing, so I will agree 2 MW with them.',
      apply: true,
      edit: (s) => {
        s.hubMw = 2;
      },
      change: 'Hub at 2 MW',
    },
  ],
};

export function resolve<T>(v: T | ((ctx: AgentContext) => T), ctx: AgentContext): T {
  return typeof v === 'function' ? (v as (ctx: AgentContext) => T)(ctx) : v;
}
